import { capture } from "@/lib/posthog";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiFetch, apiFetchRaw, post, put, del, ApiError } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";
import { appendListParams, unwrapList, type ListParams, type ListResponse } from "@/lib/list-params";
import type { RunSeverity } from "@/lib/run-output";
import type { WorkflowLabel } from "@/hooks/use-workflow-labels";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ExecutionTier = "instant" | "standard" | "long";

/**
 * The three values the list's Status column renders, as a filter.
 *
 * "unpublished" is not a third kind of enabled flag: a workflow with no
 * published version cannot run whatever `enabled` says (/execute answers 409
 * WORKFLOW_NOT_PUBLISHED), so the cell reads "Not published" for it and this
 * filter matches the cell. Applied by the server — see useWorkflowsList.
 */
export type WorkflowStatusFilter = "active" | "paused" | "unpublished";

/** Recency window for the Updated column's filter, evaluated on the DB clock. */
export type WorkflowUpdatedWithin = "24h" | "7d" | "30d";

/**
 * The list columns whose header can order the table.
 *
 * Sorting is applied by the server for the same reason the filters are: the
 * list is paged, so ordering the loaded page would sort a page rather than a
 * table — "oldest first" would answer with the oldest of the newest 25.
 */
export type WorkflowSortKey = "name" | "status" | "latest" | "updated";
export type SortDir = "asc" | "desc";

export const WORKFLOW_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: "" | WorkflowStatusFilter; label: string }> = [
  { value: "", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "unpublished", label: "Not published" },
];

export const WORKFLOW_UPDATED_WITHIN_OPTIONS: ReadonlyArray<{ value: "" | WorkflowUpdatedWithin; label: string }> = [
  { value: "", label: "Any time" },
  { value: "24h", label: "Updated last 24h" },
  { value: "7d", label: "Updated last 7d" },
  { value: "30d", label: "Updated last 30d" },
];

export type ExecutionStatus =
  | "pending"
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "timeout"
  | "cancelled";

export interface RetryPolicy {
  maxAttempts: number;
  initialIntervalMs: number;
  backoffCoefficient: number;
  maximumIntervalMs: number;
}

/** One environment's currently-live deployment, as returned by the list endpoint. */
export interface ActiveDeployment {
  envSlug: string;
  envName: string;
  isDefault: boolean;
  /** The PUBLISHED version number running there (workflow_versions.version_number). */
  versionNumber: number;
  /** How many times this workflow has been deployed to this environment. */
  envVersion: number;
  deployedAt: string | null;
}

export interface Workflow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  code: string;
  draftCode: string | null;
  devCode: string | null;
  devVersion: number;
  devPublishedAt: string | null;
  devPublishedBy: string | null;
  livePublishedAt: string | null;
  livePublishedBy: string | null;
  inputSchema: Record<string, unknown> | null;
  /** JSON Schema describing the shape the workflow returns (output contract).
   *  Edited in the builder's Contract tab. Null = never set; the editor falls
   *  back to an empty schema template. Sibling to inputSchema (input contract). */
  outputSchema: Record<string, unknown> | null;
  /** Example JSON authored in the Contract tab's JSON view for the input. The
   *  input schema is derived from it, but both are stored so the JSON view
   *  round-trips exactly on reopen. Any JSON value. Null = never set. */
  inputExample: unknown;
  /** Same, for the output contract. */
  outputExample: unknown;
  executionTier: ExecutionTier;
  timeoutMs: number;
  retryPolicy: RetryPolicy;
  escalateOnTimeout: boolean;
  enabled: boolean;
  /**
   * `workflows.version` — a SAVE counter, not a release number. Bumped on every
   * PUT that changes anything, and overwritten with `env_version` when the
   * workflow is deployed to the default environment. It is not what runs and it
   * is not what deploys; use `devVersion` for "latest published" and
   * `activeDeployments` for "what is live where".
   */
  version: number;
  /**
   * Which published version is live in each environment right now, newest
   * deployment per environment. Empty when the workflow has never been deployed
   * anywhere — which is the normal state, since the default `test` environment
   * runs the latest published snapshot rather than a deployment.
   *
   * Optional because the dashboard deploys independently of the workflow
   * service: during a rollout, or if that service alone is rolled back, the
   * list returns rows without this field. Treating it as guaranteed would throw
   * on `.find()` and blank the entire Workflows table.
   */
  activeDeployments?: ActiveDeployment[];
  /** Labels carried by this workflow, at most MAX_WORKFLOW_LABELS. Attached
   *  by the list endpoint (batched, not per row); absent on payloads that
   *  do not join them, hence optional. */
  labels?: WorkflowLabel[];
  /** The one category this workflow sits under, or null for Uncategorised —
   *  which is a normal place to be, not a missing value. */
  categoryId?: string | null;
  orgId: string;
  createdBy: string;
  testCases: Array<{ id: string; group: string; scenario: string; pass: string; mode?: "live" | "mock" }> | null;
  /** Raw JSON text the developer typed into the ctx.input editor on the Test tab.
   *  Persisted per-workflow so it survives Save Workflow + reopen and does not
   *  leak into other workflows. Null = never saved; frontend falls back to the
   *  empty template. */
  testInput: string | null;
  /** Same as testInput, for ctx.headers. */
  testHeaders: string | null;
  lastValidation: {
    status: "pass" | "partial" | "fail";
    mode: "mock" | "live";
    ranAt: string;
    ranBy: string;
    results: Array<{ id: string; status: "pass" | "fail" | "skipped" | "partial"; evidence?: unknown; error?: string; fix?: string }>;
    cleanup?: string;
  } | null;
  documentation: {
    dataFlowMermaid: string;
    sequenceMermaid: string;
    requirementsMarkdown: string;
    flowSteps?: Array<{ title: string; description: string; type: string; app?: string; from?: string; to?: string; yesLabel?: string; noLabel?: string; yesStep?: { title: string; description: string; type: string; app?: string; fields?: string[] } | null; noStep?: { title: string; description: string; type: string; app?: string; fields?: string[] } | null; fields?: string[]; triggerInfo?: string; returnFields?: string[] }>;
    sequencePhases?: { participants: string[]; phases: Array<{ title: string; subtitle: string; messages: Array<{ from: string; to: string; label: string; description: string; type: string }> }> } | unknown;
    summary: string;
    generatedAt: string;
  } | null;
  connectorManifest: ManifestEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface ManifestAction {
  slug: string;
  id?: string;
  version?: string;
  /** True for actions the developer added by hand from the manifest editor.
   *  Survives the server's code-refresh merge (the static extractor wouldn't
   *  find them because the call isn't in the saved code). */
  manual?: boolean;
  /** This workflow's retry/timeout override for THIS action — the highest
   *  layer, outranking the connector-level entry and the connector row.
   *  Partial: unset fields inherit. null = no override. */
  retryPolicy?: Record<string, unknown> | null;
  timeoutMs?: number | null;
}

export interface ManifestEntry {
  slug: string;
  connectorId?: string;
  /** Owning-org of the connector definition (e.g. 'org_platform'). */
  orgId?: string;
  /** Actions the workflow code calls on this connector. */
  actions?: ManifestAction[];
  scope?: "SAAS" | "MULTI_TENANT";
  connectionId?: string | null;
  stale?: boolean;
  /** This workflow's retry/timeout override for every action on this connector,
   *  layered over the connector row. Partial: unset fields inherit. */
  retryPolicy?: Record<string, unknown> | null;
  timeoutMs?: number | null;
}

export interface LogEntry {
  level: string;
  message: string;
  offsetMs: number;
  /** Set only on the capture-limit marker the workflow service inserts when it
   *  drops entries. Not a line the workflow wrote — excluded from counts. */
  captureLimit?: true;
}

export interface TraceEntry {
  type: "call" | "log";
  /** Set for fastn.connector.* calls — the row renders the full request/response
   *  instead of the truncated ctx-call preview. */
  kind?: "connector";
  step: string;
  args: string;
  input: unknown[];
  status: "ok" | "error";
  offsetMs: number;
  durationMs: number;
  result: unknown;
  error?: string;
  level?: "info" | "warn" | "error";
  message?: string;
  /** Set only on the capture-limit marker the workflow service inserts when it
   *  drops entries. Not a bridge call the workflow made. */
  captureLimit?: true;
}

export interface TraceSummary {
  totalSteps: number;
  succeeded: number;
  failed: number;
  slowestMs: number;
  slowestStep: string;
  /** How many times the run actually executed. Written by the workflow service
   *  ONLY once a run has retried (see withAttempts / recordExecutionAttempts),
   *  so absent means "no retry was observed", not "ran zero times". */
  attempts?: number;
}

export type ErrorCategory = 'user_data' | 'configuration' | 'system' | 'dependency' | 'unknown_outcome' | 'oom';

export interface FixAction {
  type: 'reconnect_connector' | 'reauthenticate_connector' | 'open_connector_settings' | 'retry_workflow' | 'check_input_data';
  connectorSlug?: string;
  label: string;
}

export interface FixCard {
  icon: string;
  title: string;
  description: string;
  action?: FixAction;
}

// Persisted LLM explanation of a run (workflow_executions.trace_summary).
// Written by two producers in services/workflow/src/lib/llm-diagnosis.ts:
// failure diagnosis (no `kind`) and the completed-run partial-failure summary
// (`kind: 'partial'`). `severity` says whether the user can wait it out or
// must act.
export interface AiDiagnosis {
  kind?: "partial";
  label?: string;
  title?: string;
  whatHappened?: string;
  howToFix?: string;
  /** Written by a model into trace_summary jsonb, so the value on the wire is whatever it
   *  emitted -- including severities written before "platform" existed. Narrow with
   *  asRunSeverity() before using it; the union here is the contract, not a guarantee. */
  severity?: RunSeverity;
  issueCount?: number;
}

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  status: ExecutionStatus;
  statusCode: number | null;
  input: Record<string, unknown> | null;
  output: unknown;
  error: string | null;
  errorCategory: ErrorCategory | null;
  aiDiagnosis: AiDiagnosis | null;
  fixSuggestion: string | null;
  fixAction: FixAction | null;
  logs: LogEntry[] | null;
  // Null on LIST responses, populated on DETAIL. The list endpoints project a
  // narrow column set and leave both bodies out, so `null` here means "not
  // loaded" — never "no logs" / "no trace". Use `hasTrace` to decide whether
  // there is anything to expand, then fetch the body with useWorkflowExecution.
  trace: TraceEntry[] | null;
  // Optional because the bundle and the workflow pods roll independently: a new
  // bundle can talk to a not-yet-rolled pod that still sends `trace` and no
  // `hasTrace`. Read it through executionHasTrace(), never directly.
  hasTrace?: boolean;
  summary: TraceSummary | null;
  requestHeaders: Record<string, string> | null;
  /** True when the payload fields above were withheld for this viewer's role
   *  (see canReadExecutionBodies in the workflow service). Distinguishes
   *  "you may not see this" from "the run produced nothing". Optional: an
   *  older workflow pod does not send it. */
  bodiesRedacted?: boolean;
  // The tier this run actually executed at, which is not always the workflow's
  // current tier: an escalated attempt ran one tier up, and the workflow may have
  // been reconfigured since. Optional because it is null on rows written before
  // the column existed.
  executionTier?: string | null;
  // The published version whose frozen code this run executed. Null on rows
  // written before the column existed, and on any run whose code could not be
  // traced to a published version — the UI shows no version rather than a
  // wrong one. Never the workflow's `version` field, which counts saves.
  workflowVersion?: number | null;
  /** Set when this run is a replay — the execution it re-ran. */
  replayedFromExecutionId?: string | null;
  durationMs: number | null;
  triggeredBy: string;
  depth: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

/**
 * Does this execution have a trace worth expanding?
 *
 * Prefers the `hasTrace` flag the LIST endpoints now send, and falls back to the
 * trace body itself for a response that predates it. The fallback is what keeps
 * the History expand affordance alive against a workflow pod that has not rolled
 * yet — without it, `hasTrace` is undefined on every row and the whole panel is
 * silently non-clickable.
 */
export function executionHasTrace(
  e: Pick<WorkflowExecution, "hasTrace" | "trace">,
): boolean {
  return e.hasTrace ?? (e.trace?.length ?? 0) > 0;
}

/**
 * Which execution id, if any, useWorkflowExecution should fetch for the
 * workflow currently on screen.
 *
 * The detail route filters on `workflow_id`, so an execution id expanded under
 * workflow A paired with workflow B's id addresses a row that cannot exist —
 * the request 404s and gets retried. Deriving the id from the (workflow,
 * execution) pair the user actually expanded means the mismatched pair never
 * reaches the query. Clearing the id in an effect does not achieve this: React
 * flushes the query's own `observer.setOptions` effect later in the same
 * commit, so the stale pair has already been fetched by the time the clearing
 * effect's re-render lands.
 */
export function expandedExecutionId(
  expanded: { wfId: string; execId: string } | null,
  activeWfId: string | undefined,
): string | null {
  return expanded && expanded.wfId === activeWfId ? expanded.execId : null;
}

export interface WorkflowVersion {
  id: string;
  versionNumber: number;
  message: string | null;
  publishedBy: string;
  publishedAt: string;
  gitCommitSha: string | null;
  githubPrNumber: number | null;
  githubReleaseId: string | null;
  githubReleaseUrl: string | null;
}

export interface WorkflowPromotion {
  id: string;
  kind: "promote" | "publish";
  envId: string | null;
  envName: string | null;
  envSlug: string | null;
  isDefault: boolean | null;
  versionId: string | null;
  versionNumber: number | null;
  status: "pending" | "completed" | "rejected" | "stale";
  prNumber: number | null;
  prUrl: string | null;
  branchName: string;
  requestedBy: string;
  requestedAt: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
}

export interface WorkflowEnvironment {
  id: string;
  name: string;
  slug: string;
  isDefault: boolean;
  isDeletable: boolean;
  requiresReview: boolean;
}

export interface WorkflowDeployment {
  id: string;
  envId: string;
  envName: string;
  envSlug: string;
  isDefault: boolean;
  versionId: string;
  versionNumber: number;
  versionMessage: string | null;
  envVersion: number;
  isActive: boolean;
  deployedBy: string;
  deployedAt: string;
  versionPublishedAt: string;
}

export interface TestResult {
  result: unknown;
  logs: LogEntry[];
  trace: TraceEntry[];
  durationMs: number;
  summary: TraceSummary;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useWorkflows(tenantOverride?: string, opts?: { enabled?: boolean }) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  // tenantOverride: when set, ask the gateway to forward the workflow query
  // under the OVERRIDE org id (used by the dashboard's "preview as
  // customer" mode). The gateway validates that the override is a child
  // of the caller's org; otherwise the override is ignored.
  const qs = tenantOverride ? `?orgId=${encodeURIComponent(tenantOverride)}` : "";

  return useQuery({
    queryKey: keys.workflows.list(orgId, tenantOverride),
    queryFn: () => apiFetch<Workflow[]>(`${API_BASE}/api/v1/workflows${qs}`),
    enabled: opts?.enabled ?? !!orgId,
  });
}

/**
 * Server-searched, server-paginated workflows — what the Workflows list
 * renders.
 *
 * Sibling of `useWorkflows()` for the same reason `useConnectorsList` is a
 * sibling of `useConnectors()`: the unpaginated hook feeds the trigger forms'
 * workflow pickers and the Executions page's workflow filter, which need every
 * row. A picker silently missing the workflow you are looking for is a worse
 * bug than a list without a pager.
 */
export function useWorkflowsList(
  params: ListParams & {
    tenantOverride?: string;
    deleted?: boolean;
    executionTier?: ExecutionTier;
    labelIds?: string[];
    status?: WorkflowStatusFilter;
    updatedWithin?: WorkflowUpdatedWithin;
    sort?: WorkflowSortKey;
    dir?: SortDir;
    groupByCategory?: boolean;
  } = {},
) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const {
    search, limit, offset, tenantOverride, deleted, executionTier, labelIds, status, updatedWithin, sort, dir,
    groupByCategory,
  } = params;

  const qs = appendListParams(new URLSearchParams(), { search, limit, offset });
  if (tenantOverride) qs.set("orgId", tenantOverride);
  if (deleted) qs.set("deleted", "true");
  if (executionTier) qs.set("executionTier", executionTier);
  // Both are server-side for the same reason tier and labels are: the list is
  // paged, so narrowing the loaded page in the browser would make the count and
  // the pager disagree with what is on screen.
  if (status) qs.set("status", status);
  if (updatedWithin) qs.set("updatedWithin", updatedWithin);
  // No sort param at all means the list keeps its default order (newest
  // created first), so a header that has never been clicked changes nothing.
  if (sort) { qs.set("sort", sort); qs.set("dir", dir ?? "desc"); }
  // Repeated ?label= rather than a joined value: the server ANDs one EXISTS
  // per occurrence, and a comma-joined string would be read as one id.
  for (const id of labelIds ?? []) qs.append("label", id);
  // Only changes the server's ORDER BY, so a category's rows are contiguous
  // and a group cannot straddle a page boundary and reappear under a second
  // copy of its own header. The client decides whether to DRAW the groups.
  if (groupByCategory) qs.set("groupBy", "category");

  return useQuery({
    queryKey: [
      ...keys.workflows.list(orgId, tenantOverride),
      search ?? null,
      deleted ?? false,
      executionTier ?? null,
      (labelIds ?? []).join(",") || null,
      status ?? null,
      updatedWithin ?? null,
      sort ?? null,
      sort ? dir ?? "desc" : null,
      // In the key: it changes the ORDER of the returned page, so a cached
      // ungrouped page must not be served for a grouped request.
      groupByCategory ?? false,
      limit ?? null,
      offset ?? null,
    ] as const,
    queryFn: () => apiFetchRaw<ListResponse<Workflow>>(`${API_BASE}/api/v1/workflows?${qs.toString()}`),
    enabled: !!orgId,
    placeholderData: (prev) => prev,
    select: unwrapList,
  });
}

export function useWorkflow(id: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.workflows.detail(orgId, id),
    queryFn: () => apiFetch<Workflow>(`${API_BASE}/api/v1/workflows/${id}`),
    enabled: !!orgId && !!id,
  });
}

export function useWorkflowExecutions(wfId: string | undefined, limit = 20, tenantOverride?: string) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const overrideQs = tenantOverride ? `&orgId=${encodeURIComponent(tenantOverride)}` : "";

  return useQuery({
    queryKey: [...keys.workflows.executions(orgId, wfId), tenantOverride ?? null] as const,
    queryFn: () => {
      if (!wfId) return Promise.resolve([] as WorkflowExecution[]);
      return apiFetch<WorkflowExecution[]>(
        `${API_BASE}/api/v1/workflows/${wfId}/executions?limit=${limit}${overrideQs}`,
      );
    },
    enabled: !!orgId && !!wfId,
  });
}

/** Most recent runs across every workflow in the org — the home overview's
 *  "recent activity" feed. Unfiltered by status: the point is what happened,
 *  not what broke. */
export function useRecentExecutions(limit = 6) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: [...keys.workflows.list(orgId), 'recent-executions', limit] as const,
    queryFn: () =>
      apiFetch<(WorkflowExecution & { workflowName?: string })[]>(
        `${API_BASE}/api/v1/workflows/executions?limit=${limit}`,
      ),
    enabled: !!orgId,
    staleTime: 30_000,
  });
}

// IN1: every insights surface must describe the SAME failure universe, or
// the panel contradicts itself. The backend counts failed|timeout|cancelled
// (services/api widget-insights.ts); this list used to ask for `status=failed`
// alone over an unbounded window, so a timed-out run produced "1 needs
// attention" in the hero and "All clear — no failed runs" in the drill-down.
/** What the insights surfaces count as "needs attention" — matches the
 *  backend's `status IN ('failed','timeout','cancelled')`. */
export const FAILED_RUN_STATUSES = "failed,timeout,cancelled";

/** What an *error* list should show. A run a user deliberately cancelled is
 *  not an error, and including it let cancellations crowd real failures out
 *  of the small `limit` the widget error tab fetches. */
export const ERROR_RUN_STATUSES = "failed,timeout";

/** Exported so the query contract is unit-testable without a React tree —
 *  this URL drifting out of step with the backend's failure definition is
 *  precisely the bug this guards. */
export function buildFailedExecutionsUrl(
  limit: number, tenantOverride?: string, dateFrom?: string,
  statuses: string = FAILED_RUN_STATUSES,
): string {
  const overrideQs = tenantOverride ? `&orgId=${encodeURIComponent(tenantOverride)}` : "";
  // Scopes the list to the same window as the hero card. Filtering server-side
  // (rather than trimming the response) keeps the `limit` budget spent on runs
  // that are actually in-window.
  const dateFromQs = dateFrom ? `&dateFrom=${encodeURIComponent(dateFrom)}` : "";
  return `${API_BASE}/api/v1/workflows/executions?status=${statuses}&limit=${limit}${overrideQs}${dateFromQs}`;
}

export function useRecentFailedExecutions(
  limit = 50, tenantOverride?: string, dateFrom?: string,
  statuses: string = FAILED_RUN_STATUSES,
) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: [...keys.workflows.list(orgId), 'recent-failed', limit, tenantOverride ?? null, dateFrom ?? null, statuses] as const,
    queryFn: () =>
      apiFetch<(WorkflowExecution & { workflowName?: string })[]>(
        buildFailedExecutionsUrl(limit, tenantOverride, dateFrom, statuses),
      ),
    enabled: !!orgId,
    staleTime: 30_000,
  });
}

export function useWorkflowExecutionsPaginated(wfId: string | undefined, limit = 20, offset = 0) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: [...keys.workflows.executions(orgId, wfId), 'paginated', limit, offset] as const,
    queryFn: () => {
      if (!wfId) return Promise.resolve([] as WorkflowExecution[]);
      return apiFetch<WorkflowExecution[]>(
        `${API_BASE}/api/v1/workflows/${wfId}/executions?limit=${limit}&offset=${offset}`,
      );
    },
    enabled: !!orgId && !!wfId,
  });
}

/**
 * One execution, in full — the DETAIL route, which is the only place the log
 * and trace bodies live. The LIST routes project a narrow column set, so
 * anything that needs a body has to come through here.
 *
 * This is deliberately a single hook. Two hooks over one query key are one
 * cache entry in React Query, so the query-level options (queryFn, retry,
 * gcTime) are whichever observer wrote last — and the two call sites in
 * WorkflowsPage collide for real whenever the execution being polled is also
 * the one expanded in History.
 *
 * Pass `enabled: false` to hold the fetch until the row is actually expanded —
 * a trace can be megabytes and there is no reason to pull one per visible row.
 * `refetchInterval` keeps a still-running execution live and stops itself the
 * moment the status goes terminal; `staleTime` is what stops a re-expand from
 * re-pulling a body that has not changed.
 */
export function useWorkflowExecution(
  wfId: string | undefined,
  execId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.workflows.execution(orgId, wfId, execId),
    queryFn: () => {
      if (!wfId || !execId) return Promise.resolve(undefined as unknown as WorkflowExecution);
      return apiFetch<WorkflowExecution>(
        `${API_BASE}/api/v1/workflows/${wfId}/executions/${execId}`,
      );
    },
    enabled: (opts.enabled ?? true) && !!orgId && !!wfId && !!execId,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) return false;
      return ["queued", "running"].includes(status) ? 2000 : false;
    },
  });
}

export function useWorkflowVersions(wfId: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.workflows.versions(orgId, wfId),
    queryFn: () =>
      apiFetch<WorkflowVersion[]>(
        `${API_BASE}/api/v1/workflows/${wfId}/versions`,
      ),
    enabled: !!orgId && !!wfId,
  });
}

export function useWorkflowEnvironments() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.workflows.environments(orgId),
    queryFn: () =>
      apiFetch<WorkflowEnvironment[]>(`${API_BASE}/api/v1/environments`),
    enabled: !!orgId,
    staleTime: 60_000, // environments change rarely
  });
}

export function useCreateEnvironment() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (input: { name: string; slug: string }) =>
      apiFetch<WorkflowEnvironment>(`${API_BASE}/api/v1/environments`, post(input)),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workflows.environments(orgId) }),
  });
}

export function useUpdateEnvironmentReview() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: ({ envId, requiresReview }: { envId: string; requiresReview: boolean }) =>
      apiFetch<WorkflowEnvironment>(`${API_BASE}/api/v1/environments/${envId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requiresReview }),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workflows.environments(orgId) }),
  });
}

export function useDeleteEnvironment() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (envId: string) =>
      apiFetch<{ deleted: true }>(`${API_BASE}/api/v1/environments/${envId}`, del),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workflows.environments(orgId) }),
  });
}

export function useWorkflowDeployments(wfId: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.workflows.deployments(orgId, wfId),
    queryFn: () =>
      apiFetch<Record<string, WorkflowDeployment[]>>(
        `${API_BASE}/api/v1/workflows/${wfId}/deployments`,
      ),
    enabled: !!orgId && !!wfId,
  });
}

export function useWorkflowPromotions(wfId: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.workflows.promotions(orgId, wfId),
    queryFn: () =>
      apiFetch<WorkflowPromotion[]>(
        `${API_BASE}/api/v1/workflows/${wfId}/promotions`,
      ),
    enabled: !!orgId && !!wfId,
    // A PR merge is a human-timescale event, not a job to poll aggressively —
    // only poll while something is actually awaiting review.
    refetchInterval: (query) => {
      const rows = query.state.data;
      return rows?.some((r) => r.status === "pending") ? 15_000 : false;
    },
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export interface CreateWorkflowInput {
  slug: string;
  name: string;
  description?: string;
  code: string;
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  inputExample?: unknown;
  outputExample?: unknown;
  executionTier?: ExecutionTier;
  timeoutMs?: number;
  retryPolicy?: RetryPolicy;
  escalateOnTimeout?: boolean;
  enabled?: boolean;
  testCases?: Workflow["testCases"];
  configId?: string;
  // Manual manifest entries the developer added in the Connectors tab
  // BEFORE the workflow exists. Merged server-side with the auto-extracted
  // result; auto-extracted entries win on slug collisions.
  connectorManifest?: ManifestEntry[];
}

export function useCreateWorkflow() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (body: {
      slug: string;
      name: string;
      description?: string;
      code: string;
      inputSchema?: Record<string, unknown>;
      outputSchema?: Record<string, unknown>;
      inputExample?: unknown;
      outputExample?: unknown;
      executionTier: ExecutionTier;
      retryPolicy?: RetryPolicy;
      escalateOnTimeout?: boolean;
      enabled?: boolean;
    }) => apiFetch<Workflow>(`${API_BASE}/api/v1/workflows`, post(body)),
    onSuccess: () => {
      // `source` is what answers whether the AI-native positioning is real or
      // decorative: agent leverage is the share of workflows the agent wrote,
      // and it cannot be recovered afterwards because a finished workflow looks
      // identical whoever authored it. The orchestrator creates through the
      // same endpoint, so this counts both.
      capture("workflow_created", { source: "manual" });
      queryClient.invalidateQueries({ queryKey: keys.workflows.all(orgId) });
    },
  });
}

export function useUpdateWorkflow(id: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (
      body: Partial<{
        name: string;
        description: string;
        code: string;
        inputSchema: Record<string, unknown> | null;
        outputSchema: Record<string, unknown> | null;
        inputExample: unknown;
        outputExample: unknown;
        executionTier: ExecutionTier;
        retryPolicy: RetryPolicy;
        escalateOnTimeout: boolean;
        enabled: boolean;
        testCases: Workflow["testCases"];
        documentation: Workflow["documentation"];
        connectorManifest: ManifestEntry[];
      }>,
    ) => apiFetch<Workflow>(`${API_BASE}/api/v1/workflows/${id}`, put(body)),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workflows.all(orgId) }),
  });
}

// Re-runs the server-side connector extractor against the workflow's saved
// code and merges the result with the existing manifest (preserves the
// developer's scope / connectionId choices). Useful for older workflows whose
// manifest predates the extractor.
export function useRefreshWorkflowManifest(id: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: () =>
      apiFetch<{ connectorManifest: ManifestEntry[] }>(
        `${API_BASE}/api/v1/workflows/${id}/manifest/refresh`,
        post({}),
      ),
    // Write the new manifest into every cached workflow snapshot for this id
    // before the invalidation-driven refetch lands. The refetch is still
    // needed (updated_at moves, anything else the server touches), but the
    // synchronous setQueryData makes the panel render the new entries the
    // moment the response arrives — without it, the panel waits a full
    // round-trip before the new chips appear, and depending on how the
    // parent page syncs `selectedWf` the chips may never appear at all.
    onSuccess: (data) => {
      if (!id) return;
      const nextManifest = data.connectorManifest ?? [];
      // Patch the detail query.
      queryClient.setQueryData<Workflow>(
        keys.workflows.detail(orgId, id),
        (prev) => (prev ? { ...prev, connectorManifest: nextManifest } : prev),
      );
      // Patch the list query: replace the matching workflow's manifest.
      queryClient.setQueriesData<Workflow[]>(
        { queryKey: keys.workflows.all(orgId) },
        (prev) =>
          Array.isArray(prev)
            ? prev.map((w) => (w.id === id ? { ...w, connectorManifest: nextManifest } : w))
            : prev,
      );
      queryClient.invalidateQueries({ queryKey: keys.workflows.all(orgId) });
    },
  });
}

export type DocsAudience = "end_user" | "technical";

export interface WorkflowDocs {
  workflowId: string;
  audience: DocsAudience;
  generatedAt: string | null;
  stale: boolean;
  workflowUpdatedAt: string | null;
  workflowVersion: number;
  overridden: boolean;
  overrideEditedAt?: string | null;
  overrideBasedOnGeneratedAt?: string | null;
  generatedDocChanged?: boolean;
  markdown: string;
  generatedMarkdown?: string;
}

/** Outcome of the last generation attempt, echoed by the 404 when there is no
 *  doc. `null` means no attempt has been recorded for this audience. */
export interface DocGenerationStatus {
  status: "ok" | "failed";
  at: string;
  error?: string;
}

/** The "no doc yet" case, carrying why the last attempt failed if it did. */
export interface WorkflowDocsMissing {
  missing: true;
  lastGeneration: DocGenerationStatus | null;
}

export function docsMissing(d: WorkflowDocs | WorkflowDocsMissing | undefined): d is WorkflowDocsMissing {
  return !!d && (d as WorkflowDocsMissing).missing === true;
}

/** Resolved documentation for one audience (override wins over generated).
 *
 *  A 404 means that audience's doc is not there — surfaced as a `missing`
 *  object rather than an error so the panel can offer a Generate button. It
 *  carries `lastGeneration` because "not generated yet" and "generation failed"
 *  are the same 404: without it the panel could only keep polling, which is
 *  what left it sitting on "Generating..." indefinitely after a failed run. */
export function useWorkflowDocs(id: string | undefined, audience: DocsAudience, opts?: { poll?: boolean }) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.workflows.docs(orgId, id, audience),
    queryFn: async (): Promise<WorkflowDocs | WorkflowDocsMissing> => {
      try {
        return await apiFetch<WorkflowDocs>(
          `${API_BASE}/api/v1/workflows/${id}/docs?audience=${audience}&format=structured`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          const last = err.body?.lastGeneration as DocGenerationStatus | null | undefined;
          return { missing: true, lastGeneration: last ?? null };
        }
        throw err;
      }
    },
    enabled: !!orgId && !!id,
    // Generation is async (202 then a background LLM call) — the panel polls
    // while it waits for the doc to land.
    refetchInterval: opts?.poll ? 5000 : undefined,
  });
}

/** Save (markdown) or clear (null) the partner override for one audience.
 *  The override survives regeneration — it lives in its own column. */
export function useSaveDocOverride(id: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: ({ audience, markdown }: { audience: DocsAudience; markdown: string | null }) =>
      apiFetch<Workflow>(
        `${API_BASE}/api/v1/workflows/${id}`,
        put({ documentationOverride: { audience, markdown } }),
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: keys.workflows.docs(orgId, id, vars.audience) });
      if (id) queryClient.invalidateQueries({ queryKey: keys.workflows.detail(orgId, id) });
    },
  });
}

export function useGenerateDocs(id: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (audience: "end_user" | "technical" | void) =>
      apiFetch<{ generating: boolean; audience: string }>(
        `${API_BASE}/api/v1/workflows/${id}/generate-docs`,
        post(audience ? { audience } : {}),
      ),
    // Generation is async — invalidate the detail query (which holds
    // `documentation`) as well as the list, so a later refetch picks up the
    // saved doc instead of serving the pre-generation cache forever.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.workflows.all(orgId) });
      if (id) queryClient.invalidateQueries({ queryKey: keys.workflows.detail(orgId, id) });
    },
  });
}

export function useDeleteWorkflow() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ deleted: boolean; id: string }>(
        `${API_BASE}/api/v1/workflows/${id}`,
        del,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workflows.all(orgId) }),
  });
}

export function useSaveDraft(id: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ saved: boolean }>(`${API_BASE}/api/v1/workflows/${id}/draft`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workflows.detail(orgId, id) }),
  });
}

export type PublishWorkflowResult =
  | { workflow: Workflow; version: WorkflowVersion }
  | { status: "pending_review"; prUrl: string; promotionId: string };

export function usePublishWorkflow(id: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (message?: string) =>
      apiFetch<PublishWorkflowResult>(
        `${API_BASE}/api/v1/workflows/${id}/publish`,
        post({ message }),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workflows.all(orgId) }),
  });
}

export function useDeployWorkflow(id: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (body: { env_id: string; version_id: string }) =>
      apiFetch<{
        id: string;
        envId: string;
        envSlug: string;
        versionId: string;
        envVersion: number;
        deployedAt: string;
      }>(`${API_BASE}/api/v1/workflows/${id}/deploy`, post(body)),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workflows.all(orgId) }),
  });
}

export function useExecuteWorkflow(id: string | undefined, runAsOrgId?: string) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (body: {
      input?: Record<string, unknown>;
      idempotencyKey?: string;
      headers?: Record<string, string>;
    }) => {
      const { headers: extraHeaders, ...payload } = body;
      const init = post(payload);
      if (extraHeaders) {
        init.headers = { ...(init.headers as Record<string, string>), ...extraHeaders };
      }
      // When previewing a tenant, run as that tenant's org so a workflow OWNED
      // by the tenant (self-built) resolves — the gateway honors ?orgId=<tenant>
      // for privileged callers and sets the workflow-service path org to it.
      // Without it the lookup is scoped to the account and a tenant-owned
      // workflow 404s.
      const url = `${API_BASE}/api/v1/workflows/${id}/execute${runAsOrgId ? `?orgId=${encodeURIComponent(runAsOrgId)}` : ""}`;
      return apiFetch<WorkflowExecution | { executionId: string; status: string }>(
        url,
        init,
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: keys.workflows.executions(orgId, id),
      }),
  });
}

export function useCancelExecution(wfId: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (execId: string) =>
      apiFetch<{ cancelled: boolean; id: string }>(
        `${API_BASE}/api/v1/workflows/${wfId}/executions/${execId}`,
        del,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: keys.workflows.executions(orgId, wfId),
      }),
  });
}

/** Cancel a run when the workflow id varies per row — the hub's cross-flow
 *  runs browser spans workflows, so the id rides with the mutation instead of
 *  the hook. Invalidates the per-workflow list AND the integration browser's
 *  lists (keys.executions.*), which useCancelExecution does not know about. */
export function useCancelExecutionAcrossFlows() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: ({ wfId, execId }: { wfId: string; execId: string }) =>
      apiFetch<{ cancelled: boolean; id: string }>(
        `${API_BASE}/api/v1/workflows/${wfId}/executions/${execId}`,
        del,
      ),
    onSuccess: (_result, { wfId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.workflows.executions(orgId, wfId) });
      void queryClient.invalidateQueries({ queryKey: keys.executions.all(orgId) });
    },
  });
}

/** The outcome of a replay. Instant runs are finished by the time this returns
 *  (200); standard/long runs come back queued (202) and land in the list. */
export interface ReplayResult {
  executionId: string;
  replayedFrom: string;
  tier: string;
  status: string;
  statusCode?: number;
  durationMs?: number;
  output?: unknown;
  error?: string | null;
  errorCategory?: ErrorCategory | null;
  fixSuggestion?: string | null;
}

/** Re-run a finished execution with its original input, tenant binding and tier.
 *  The workflow id rides with the mutation because the executions list spans
 *  workflows, and both that list and the per-workflow one are invalidated —
 *  the replay shows up in whichever the caller is looking at. */
export function useReplayExecution() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: ({ wfId, execId }: { wfId: string; execId: string }) =>
      apiFetch<ReplayResult>(
        `${API_BASE}/api/v1/workflows/${wfId}/executions/${execId}/replay`,
        post({}),
      ),
    onSuccess: (_result, { wfId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.workflows.executions(orgId, wfId) });
      void queryClient.invalidateQueries({ queryKey: keys.executions.all(orgId) });
    },
  });
}

export function useTestWorkflow() {
  // Attempts within one editing session, so `attempt_number` says how many
  // tries it took to get a flow working rather than how many tests were ever
  // run. Module-scoped rather than per-hook: the editor remounts the hook as
  // panes change, and a per-hook counter would reset mid-session and report 1
  // forever — which is exactly the number this is meant to expose.
  return useMutation({
    mutationFn: (body: { code: string; input?: Record<string, unknown> }) =>
      apiFetch<TestResult>(`${API_BASE}/api/v1/workflows/test`, post(body)),
    onSuccess: (data) => {
      testAttempts += 1;
      capture("workflow_tested", {
        result: "pass",
        duration_ms: data.durationMs,
        attempt_number: testAttempts,
      });
    },
    onError: () => {
      testAttempts += 1;
      capture("workflow_tested", { result: "fail", duration_ms: 0, attempt_number: testAttempts });
    },
  });
}

/** See useTestWorkflow — deliberately not per-hook state. */
let testAttempts = 0;

